import { Spinner } from 'flowbite-react';
import { useSearchParams } from 'react-router-dom';

import { ClockFace } from '@/features/courts/components/kit/clock-face';
import { MatchFrame } from '@/features/courts/components/kit/match-frame';
import { PeriodLabel } from '@/features/courts/components/kit/period-label';
import { ScoreBlock } from '@/features/courts/components/kit/score-block';
import { TeamPair } from '@/features/courts/components/kit/team-pair';
import { useCourtMatch } from '@/features/courts/hooks/use-court-match';
import { useActionCable } from '@/lib/action-cable';
import type { Court } from '@/types/api';
import { clockSpec } from '@/types/sports/clock';

import { intervalLabel, isInterval } from './interval';
import { periodLabelFor } from './period-text';

export const ClockDisplay = ({ court }: { court: Court }) => {
  const [searchParams] = useSearchParams();
  const cable = useActionCable();
  const { match, clocks } = useCourtMatch(court.id, cable, clockSpec);

  if (!match) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const { config, state } = match;
  const swapped = searchParams.has('inverted') ? !state.swapped : state.swapped;

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-between overflow-hidden font-display leading-none">
      <MatchFrame
        variant="display"
        courtName={court.name}
        label={match.label}
        scheduledAt={match.scheduled_at}
      />

      <div className="flex flex-col items-center gap-[2vh]">
        {isInterval(state.match_state) ? (
          <>
            <ClockFace
              clock={clocks.interval_time}
              direction="down"
              variant="display"
            />
            <div className="text-[12vh] uppercase text-slate-700">
              {intervalLabel(state.match_state, config.period_label)}
            </div>
          </>
        ) : (
          <>
            <ClockFace
              clock={clocks.period_time}
              direction={config.clock_direction}
              variant="display"
            />
            <PeriodLabel {...periodLabelFor(state, config)} variant="display" />
          </>
        )}
      </div>

      <TeamPair
        swapped={swapped}
        className="border-t px-[5vw] py-[2vh]"
        home={
          <ScoreBlock
            team={match.home_team}
            score={state.scores.home_team}
            variant="display"
          />
        }
        away={
          <ScoreBlock
            team={match.away_team}
            score={state.scores.away_team}
            variant="display"
            reverse
          />
        }
      />
    </div>
  );
};
